import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiMap, FiUsers, FiGlobe, FiInfo } = FiIcons;

const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const VisitorMap = ({ locations = [] }) => {
  const [tooltip, setTooltip] = useState(null);

  const totalVisitors = locations.reduce((acc, loc) => acc + loc.count, 0);

  return (
    <div className="bg-white p-8 md:p-12 rounded-[40px] shadow-sm border border-brand-dark overflow-hidden relative">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-accent-gold/10 rounded-2xl flex items-center justify-center text-accent-gold border border-accent-gold/20">
            <SafeIcon icon={FiMap} className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-2xl font-serif text-accent-navy">Global Reach</h3>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-text-muted">Impact across borders</p>
          </div>
        </div>

        <div className="flex items-center gap-8">
          <div className="text-center">
            <p className="text-2xl font-serif text-accent-navy">{totalVisitors}</p>
            <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">Total Visitors</p>
          </div>
          <div className="w-px h-10 bg-brand-dark" />
          <div className="text-center">
            <p className="text-2xl font-serif text-accent-navy">{locations.length}</p>
            <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">Cities Represented</p>
          </div>
        </div>
      </div>

      <div className="relative bg-brand-light/30 rounded-3xl border border-brand-dark p-4 md:p-8 min-h-[300px] md:min-h-[450px]">
        <ComposableMap
          projectionConfig={{ scale: 160 }}
          className="w-full h-full"
        >
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill="#E5E7EB"
                  stroke="#FFFFFF"
                  strokeWidth={0.5}
                  style={{
                    default: { outline: "none" },
                    hover: { fill: "#D1D5DB", outline: "none" },
                    pressed: { outline: "none" },
                  }}
                />
              ))
            }
          </Geographies>

          {locations.map(({ name, coordinates, count, type }) => (
            <Marker 
              key={name} 
              coordinates={coordinates}
              onMouseEnter={() => setTooltip({ name, count, type })}
              onMouseLeave={() => setTooltip(null)}
            >
              <motion.circle
                initial={{ r: 0 }}
                animate={{ r: Math.max(3, Math.min(10, count / 2)) }}
                fill="var(--theme-accent, #C5A880)"
                stroke="#FFF"
                strokeWidth={2}
                className="cursor-pointer"
                whileHover={{ scale: 1.5, fill: "#1B2B44" }}
              />
            </Marker>
          ))}
        </ComposableMap>

        <AnimatePresence>
          {tooltip && (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="absolute bottom-8 left-8 bg-accent-navy text-white p-4 rounded-2xl shadow-2xl z-20 border border-white/10 backdrop-blur-md"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiUsers} className="w-4 h-4 text-accent-gold" />
                </div>
                <div>
                  <p className="text-xs font-black uppercase tracking-widest">{tooltip.name}</p>
                  <p className="text-[10px] text-white/60">{tooltip.type}</p>
                </div>
              </div>
              <p className="text-sm font-serif">
                <span className="text-accent-gold font-bold">{tooltip.count}</span> visitors honored this legacy
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="absolute top-4 right-4 flex flex-col gap-2">
          <div className="bg-white/80 backdrop-blur-sm px-4 py-2 rounded-xl border border-brand-dark flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-accent-gold shadow-[0_0_8px_rgba(197,168,128,0.8)] animate-pulse" />
            <span className="text-[9px] font-black uppercase tracking-widest text-accent-navy">Live Viewers</span>
          </div>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-6 items-center justify-center">
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiGlobe} className="w-4 h-4 text-text-muted" />
          <span className="text-[10px] font-black uppercase tracking-widest text-text-muted">Connecting Hearts Globally</span>
        </div>
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiInfo} className="w-4 h-4 text-text-muted" />
          <span className="text-[10px] font-black uppercase tracking-widest text-text-muted">Aggregated Anonymous Data</span>
        </div>
      </div>
    </div>
  );
};

export default VisitorMap;